import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import useGetDevicesFiltered from '@src/hooks/api/queries/collections/useGetDevicesFiltered';
import storeConnector from '@src/store/storeConnector';
import { type Device } from '@src/types/Device';
import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import useSubscription from '@src/hooks/useSubscription';
import { DataPoint } from '@src/types/DataPoint';
import SimplePointDataTable from './SimplePointDataTable';
import { WidgetTypePointList } from '@src/types/Widget';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';

interface MeterOption {
  name: string;
  engineeringId: string;
  deviceId: string;
}

const MeterPointList = memo(({ dataPoints }: { dataPoints: DataPoint[] }) => {
  const dpWithValues = useSubscription(dataPoints);
  return (
    <Box mt={2}>
      <SimplePointDataTable data={dpWithValues} />
    </Box>
  );
});

const Meter = memo(() => {
  const metersSelectFn = useCallback((data: unknown): MeterOption[] => {
    if (!Array.isArray(data)) {
      return [];
    }

    const uniqueMeters = new Map<string, MeterOption>();
    data.forEach((meter: Device) => {
      if (!uniqueMeters.has(meter.engineeringId)) {
        uniqueMeters.set(meter.engineeringId, {
          name: meter.name,
          engineeringId: meter.engineeringId,
          deviceId: meter.deviceId,
        });
      }
    });
    return Array.from(uniqueMeters.values());
  }, []);

  const {
    data: meters,
    isSuccess: areMetersSuccess,
    isLoading: isMetersLoading,
    isError: isMetersError,
  } = useGetDevicesFiltered({
    body: {
      filter: {
        type: {
          $in: ['meter'],
        },
      },
    },
    options: {
      queryKeys: ['meters'],
      select: metersSelectFn,
    },
  });

  const meterMap = useMemo(() => {
    return (
      meters?.reduce(
        (acc, meter) => {
          acc[meter.engineeringId] = meter;
          return acc;
        },
        {} as Record<string, MeterOption>,
      ) || {}
    );
  }, [meters]);

  const [selectedMeter, setSelectedMeter] = useState<MeterOption>({
    name: '',
    engineeringId: '',
    deviceId: '',
  });

  const {
    data: widgetConfig,
    isLoading: isMeterLoading,
    isError: isMeterError,
  } = useGetInternalWidgetConfig<WidgetTypePointList>(
    `meterPoints_${selectedMeter.engineeringId}`,
    {
      sourceDeviceId: selectedMeter?.engineeringId,
    },
    {
      enabled: !!meters?.length && !!selectedMeter?.engineeringId,
      queryKeys: ['meterPoints', selectedMeter?.engineeringId || ''],
    },
  );

  const isLoading = isMetersLoading || isMeterLoading;
  const isError = isMetersError || isMeterError;

  const handleMeterChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const engineeringId = e.target.value;
      const foundMeter = meterMap[engineeringId];

      if (
        foundMeter &&
        foundMeter.engineeringId !== selectedMeter.engineeringId
      ) {
        setSelectedMeter(foundMeter);
      }
    },
    [meterMap, selectedMeter.engineeringId],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      areMetersSuccess &&
      meters &&
      meters.length > 0 &&
      !selectedMeter.engineeringId
    ) {
      setSelectedMeter(meters[0]);
    }
  }, [areMetersSuccess, meters]);

  if (isMetersLoading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {meters?.length ? (
        <Select
          disabled={isLoading || isError || !meters?.length}
          onChange={handleMeterChange}
          value={selectedMeter.engineeringId}
          variant='standard'
          renderValue={(selected) => {
            const selectedMeterFromMap = meterMap[selected];
            return selectedMeterFromMap
              ? selectedMeterFromMap.name
              : 'Select Meter';
          }}
          displayEmpty
          disableUnderline
          sx={{
            fontSize: '1.2rem',
          }}
        >
          {meters?.map((meter) => (
            <MenuItem key={meter.deviceId} value={meter.engineeringId}>
              {meter.name}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <h4>POI Meter</h4>
      )}
      {widgetConfig?.dataPoints && widgetConfig.dataPoints.length > 0 ? (
        <MeterPointList dataPoints={widgetConfig.dataPoints[0]} />
      ) : (
        <Box mt={2}>
          <Typography variant='body1' align='center'>
            No meter configuration found
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default storeConnector(Meter, {
  user: ['role'],
  config: ['siteMeta'],
});
